import { SortingState } from "@tanstack/react-table";

import { IWarehouseLocation } from "../../models/Location";
import { FirstListedBucket } from "../../models/listingOptimisation/IListingInsights";

export type LocationSearchMode = "equals" | "beginsWith";

/** Sort options the queue offers. The server orders the whole queue; the column re-sorts the page to match. */
export type QueueSortKey = "listingStarted" | "partAge" | "priceTooHigh" | "priceTooLow" | "missingData" | "overstock" | "listingReviewed";

export interface IQueueFilters {
  search: string;
  categoryId: string | null;
  /** Kept for the filter pill; the query only sends the id. */
  categoryName: string | null;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationBeginsWith: string;
  firstListedBucket: FirstListedBucket | null;
  /** No MPN and no OE number. */
  missingPartNumbers: boolean;
  /** Hide listings reviewed within this many days; null shows everything. */
  hideReviewedWithinDays: number | null;
  /** Live price more than 10% above the intelligent pricing average. */
  pricedAboveIntelligence: boolean;
  /** Individual missing-field chips (MPN, OEMPN, Brand, ...), the listings query's missingFields. */
  missingFields: string[];
  // The "work this group" filters from the Part numbers / Vehicles to work tables. Exact matches on
  // the keys the stats grouped by; a vehicle group also sets categoryId. Not saved by the lock icon.
  /** MPN or OE number equal to this. */
  partNumber: string | null;
  /** Normalised make + model range, set together. */
  vehicleMake: string | null;
  vehicleModel: string | null;
  /**
   * The MK or series the vehicle group was split on ("MK2", "E90"). Empty string is the group
   * with no MK and no series. Null when the queue is not narrowed to a vehicle group.
   */
  vehicleGeneration: string | null;
  sort: QueueSortKey;
}

export const DEFAULT_HIDE_REVIEWED_DAYS = 30;

export const defaultQueueFilters: IQueueFilters = {
  search: "",
  categoryId: null,
  categoryName: null,
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationBeginsWith: "",
  firstListedBucket: null,
  missingPartNumbers: false,
  hideReviewedWithinDays: DEFAULT_HIDE_REVIEWED_DAYS,
  pricedAboveIntelligence: false,
  missingFields: [],
  partNumber: null,
  vehicleMake: null,
  vehicleModel: null,
  vehicleGeneration: null,
  sort: "listingStarted",
};

export const QUEUE_FILTERS_STORAGE_KEY = "wilma:tableFilters:ebay-optimisation";

export interface IQueueSortOption {
  key: QueueSortKey;
  label: string;
  /** The TanStack sorting state the option maps to - the column id carries the server sort key in its meta. */
  sorting: SortingState;
}

// The column ids live in QueueRow. Listing age is the default (offers.listingStarted): the live
// listing, oldest first. The price, listing-data and stock options are server sorts on the whole
// queue, not a reshuffle of the current page. Part age keeps the server id "firstListed".
export const QUEUE_SORT_OPTIONS: IQueueSortOption[] = [
  { key: "listingStarted", label: "Listing, oldest", sorting: [{ id: "listingStarted", desc: false }] },
  // id stays "firstListed" so the server sort is unchanged. The option key is not "firstListed": a
  // lock saved against the old default falls through to Listing, oldest.
  { key: "partAge", label: "Part age, oldest", sorting: [{ id: "firstListed", desc: false }] },
  { key: "priceTooHigh", label: "Most too high", sorting: [{ id: "priceTooHigh", desc: true }] },
  { key: "priceTooLow", label: "Most too low", sorting: [{ id: "priceTooLow", desc: false }] },
  { key: "missingData", label: "Most missing listing data", sorting: [{ id: "missingData", desc: true }] },
  { key: "overstock", label: "Most overstock", sorting: [{ id: "overstock", desc: true }] },
  { key: "listingReviewed", label: "Last reviewed, oldest", sorting: [{ id: "listingReviewed", desc: false }] },
];

export function sortingForQueueSort(sort: QueueSortKey): SortingState {
  return QUEUE_SORT_OPTIONS.find((option) => option.key === sort)?.sorting ?? QUEUE_SORT_OPTIONS[0].sorting;
}

/**
 * The `searchBody` for POST /part/listings (Listed mode) from the queue's filters. Anything unset
 * is left undefined so the API applies no filter for it.
 */
export function buildQueueSearchBody(filters: IQueueFilters): Record<string, unknown> {
  const locationEquals = filters.locationSearchMode === "equals";

  return {
    categoryIds: filters.categoryId ? [filters.categoryId] : undefined,
    locationSearchMode: filters.locationSearchMode,
    warehouseLocationId: locationEquals ? filters.warehouseLocation?.id : undefined,
    warehouseLocationBeginsWith:
      !locationEquals && filters.warehouseLocationBeginsWith ? filters.warehouseLocationBeginsWith : undefined,
    firstListedBucket: filters.firstListedBucket ?? undefined,
    missingPartNumbers: filters.missingPartNumbers ? true : undefined,
    hideReviewedWithinDays:
      filters.hideReviewedWithinDays != null && filters.hideReviewedWithinDays > 0
        ? filters.hideReviewedWithinDays
        : undefined,
    pricedAboveIntelligence: filters.pricedAboveIntelligence ? true : undefined,
    missingFields: filters.missingFields.length > 0 ? filters.missingFields : undefined,
    partNumberExact: filters.partNumber || undefined,
    vehicleMakeExact: filters.vehicleMake || undefined,
    vehicleModelExact: filters.vehicleModel || undefined,
    // "" is a real filter (the no-MK group). Only omit it when there is no vehicle group.
    ...(filters.vehicleMake && filters.vehicleModel
      ? { vehicleGenerationExact: filters.vehicleGeneration ?? "" }
      : {}),
  };
}

/** Whether the queue is narrowed to a vehicle group - make and model always travel together. */
export function hasVehicleGroupFilter(filters: Pick<IQueueFilters, "vehicleMake" | "vehicleModel">): boolean {
  return !!filters.vehicleMake && !!filters.vehicleModel;
}

/**
 * The filter change for Show on a Part numbers to work row. Picking a part number drops any vehicle
 * group (the two would only AND down to nothing); clicking the showing row clears it again.
 */
export function partNumberGroupChange(filters: IQueueFilters, partNumber: string): Partial<IQueueFilters> {
  if (filters.partNumber === partNumber) return { partNumber: null };
  return { partNumber, vehicleMake: null, vehicleModel: null, vehicleGeneration: null };
}

/**
 * The filter change for Show on a Vehicles to work row. A vehicle group is make + model + MK or
 * series + category, so the category filter is set with it and cleared with it; any part number
 * filter is dropped. A group with no generation narrows to listings that have none.
 */
export function vehicleGroupChange(
  filters: IQueueFilters,
  group: { make: string; model: string; generation?: string | null; categoryId: string; categoryName: string | null }
): Partial<IQueueFilters> {
  if (isVehicleGroupActive(filters, group)) {
    return { vehicleMake: null, vehicleModel: null, vehicleGeneration: null, categoryId: null, categoryName: null };
  }
  return {
    vehicleMake: group.make,
    vehicleModel: group.model,
    vehicleGeneration: group.generation ?? "",
    categoryId: group.categoryId,
    categoryName: group.categoryName,
    partNumber: null,
  };
}

export function isVehicleGroupActive(
  filters: Pick<IQueueFilters, "vehicleMake" | "vehicleModel" | "vehicleGeneration" | "categoryId">,
  group: { make: string; model: string; generation?: string | null; categoryId: string }
): boolean {
  return (
    filters.vehicleMake === group.make &&
    filters.vehicleModel === group.model &&
    (filters.vehicleGeneration ?? "") === (group.generation ?? "") &&
    filters.categoryId === group.categoryId
  );
}

/** How many filters are narrowing the queue beyond the defaults (the reviewed window is a default, not a filter). */
export function countActiveQueueFilters(filters: IQueueFilters): number {
  let count = 0;
  if (filters.partNumber) count += 1;
  if (hasVehicleGroupFilter(filters)) count += 1;
  if (filters.categoryId) count += 1;
  if (filters.warehouseLocation?.id || filters.warehouseLocationBeginsWith) count += 1;
  if (filters.firstListedBucket) count += 1;
  if (filters.missingPartNumbers) count += 1;
  if (filters.pricedAboveIntelligence) count += 1;
  count += filters.missingFields.length;
  if (filters.search.trim()) count += 1;
  return count;
}
